import React, {
  useCallback,
  useContext,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useLabels } from '../../i18n/useLabels';
import { LABEL_KEYS } from '../../viewmodels/messages';
import type { ReorderViewModel } from '../../viewmodels/rowTypes';
import { DragHandle } from './DragHandle';
import { HandleRegistryContext } from './handleRegistry';

const DRAGGED_ROW_OPACITY = 0.4;

export type SortableRowSlots = {
  rowRef: (element: HTMLElement | null) => void;
  rowStyle: CSSProperties;
  dragHandle: ReactNode;
};

type Props = {
  id: string;
  reorder: ReorderViewModel;
  children: (slots: SortableRowSlots) => ReactNode;
};

// The row element belongs to the caller, so the sortable wiring comes back as slots to
// place on it. Keeping the caller's element keeps table and section markup valid.
export function SortableRow({ id, reorder, children }: Props) {
  const { formatMessage } = useLabels();
  const registry = useContext(HandleRegistryContext);
  const disabledReason = reorder.disabledReason
    ? formatMessage(reorder.disabledReason)
    : null;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: disabledReason !== null,
    attributes: {
      roleDescription: formatMessage({
        key: LABEL_KEYS.reorderRoleDescription,
      }),
    },
  });

  const handleRef = useCallback(
    (element: HTMLButtonElement | null) => {
      setActivatorNodeRef(element);
      registry.register(id, element);
    },
    [setActivatorNodeRef, registry, id]
  );

  return children({
    rowRef: setNodeRef,
    rowStyle: {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? DRAGGED_ROW_OPACITY : undefined,
    },
    dragHandle: (
      <DragHandle
        {...attributes}
        {...listeners}
        ref={handleRef}
        label={formatMessage(reorder.handleLabel)}
        disabledReason={disabledReason}
      />
    ),
  });
}
